import { Stack, InputAdornment } from '@mui/material';
import { isCPFOrCNPJ } from 'brazilian-values';
import { FormikProvider, useFormik } from 'formik';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';
import * as yup from 'yup';

import { getCardProcessingProviderByName , CARD_PROCESSING_PROVIDERS_ENUM } from '@woovi/card-processing-provider';
import { CardExpirationInput, CardNumberInput, TextField } from '@woovi/form';
import { useScript } from '@woovi/hooks';
import { useMutationCallbacks } from '@woovi/relay';
import { PAYMENT_METHOD_TYPE } from '@woovi/shared';
import { ActionButton, CvvIcon } from '@woovi/ui';

import type { PaymentMethodCreditCardConfirmStep_charge$key } from './__generated__/PaymentMethodCreditCardConfirmStep_charge.graphql.ts';
import type { ChargeAddPaymentMethodMutation } from '../../mutations/__generated__/ChargeAddPaymentMethodMutation.graphql.ts';
import { ChargeAddPaymentMethod } from '../../mutations/ChargeAddPaymentMethodMutation.tsx';

type PaymentActiveWaitingPaymentMethodCreditCardValues = {
  holderName: string;
  holderTaxID: string;
  number: string;
  expireDate: string;
  cvv: string;
};

type PaymentMethodCreditCardConfirmStepProps = {
  charge: PaymentMethodCreditCardConfirmStep_charge$key;
  billingAddress: any;
};

const PaymentMethodCreditCardConfirmStep = (
  props: PaymentMethodCreditCardConfirmStepProps,
): ReactNode => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const charge = useFragment<PaymentMethodCreditCardConfirmStep_charge$key>(
    graphql`
      fragment PaymentMethodCreditCardConfirmStep_charge on Charge {
        id
        status
        identifier
        availablePaymentMethods
        company {
          nameFriendly
          taxID {
            taxID
          }
        }
        customer {
          name
          taxID {
            taxID
          }
        }
        valueWithDiscount
        cardProcessingProvider
      }
    `,
    props.charge,
  );

  const [chargeAddPaymentMethod, isPendingChargeAddPaymentMethod] =
    useMutationCallbacks<ChargeAddPaymentMethodMutation>({
      name: 'ChargeAddPaymentMethod',
      mutation: ChargeAddPaymentMethod,
    });

  const handleChargeAddPaymentMethod = async (
    values: PaymentActiveWaitingPaymentMethodCreditCardValues,
  ): Promise<void> => {
    if (loading) {
      return;
    }

    setLoading(true);

    const expMonth = values.expireDate.split('/')[0];
    const expYear = values.expireDate.split('/')[1];

    const cardTokenize = {
      holderName: values.holderName,
      holderTaxID: values.holderTaxID,
      number: values.number,
      expMonth: parseInt(expMonth, 10),
      expYear: parseInt(expYear, 10),
      cvv: values.cvv,
    };

    const getCardSanitized = async () => {
      const provider = getCardProcessingProviderByName(
        charge.cardProcessingProvider,
      );

      if (!provider) {
        return cardTokenize;
      }

      const creditCardTokenizeResult = await provider.creditCardTokenize({
        cardTokenizePayload: cardTokenize,
        t,
      });

      if (!creditCardTokenizeResult.success) {
        return cardTokenize;
      }

      return {
        holderTaxID: cardTokenize.holderTaxID,
        expMonth: cardTokenize.expMonth,
        expYear: cardTokenize.expYear,
        token: creditCardTokenizeResult.token,
      };
    };

    const card = await getCardSanitized();

    const billingAddressWithCountry = {
      ...props.billingAddress,
      country: 'BR',
    };

    chargeAddPaymentMethod({
      variables: {
        input: {
          chargeId: charge.id,
          paymentMethodType: PAYMENT_METHOD_TYPE.CREDIT_CARD,
          paymentMethodCreditCardConfig: {
            value: charge.valueWithDiscount,
            billingAddress: billingAddressWithCountry,
            installmentNumber: 1,
            card,
          },
        },
      },
    });

    setLoading(false);
  };

  const initialValues = useMemo(
    () => ({
      holderName: '',
      holderTaxID: charge?.customer?.taxID?.taxID,
      number: '',
      expireDate: '',
      cvv: '',
    }),
    [],
  );

  const validationSchema = yup.object().shape({
    holderName: yup.string().required(t('Name is required')),
    holderTaxID: yup
      .string()
      .test('isCPF', t('Customer TaxID is invalid'), (value) => {
        if (!value) {
          return true;
        }

        return isCPFOrCNPJ(value);
      })
      .required(t('TaxID is required')),
    number: yup.string().required(t('Card expiration is mandatory')),
    expireDate: yup.string().required(t('Card expiration is mandatory')),
    cvv: yup.string().required(t('Card code is required')),
  });

  const formikbag =
    useFormik<PaymentActiveWaitingPaymentMethodCreditCardValues>({
      validateOnBlur: true,
      validateOnMount: true,
      enableReinitialize: true,
      initialValues,
      validationSchema,
      onSubmit: handleChargeAddPaymentMethod,
    });

  const { isValid, handleSubmit } = formikbag;

  if (
    charge?.cardProcessingProvider === CARD_PROCESSING_PROVIDERS_ENUM.CELCOIN
  ) {
    useScript({
      src: `https://js.cel.cash/checkout.min.js`,
      checkForExisting: false,
    });
  }

  return (
    <FormikProvider value={formikbag}>
      <Stack sx={{ gap: 2 }}>
        <CardNumberInput
          name='number'
          label={t('Card number')}
          placeholder={t('Card number')}
          sx={{ m: 0 }}
        />
        <Stack
          sx={{
            gap: 2,
            '@media (min-width: 600px)': {
              gap: 1.5,
              direction: 'row',
            },
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <CardExpirationInput
              name='expireDate'
              label={t('Expiration Date')}
              placeholder={t('Expiration Date')}
              fullWidth
            />
            <TextField
              name='cvv'
              label={t('CVV')}
              placeholder={t('CVV')}
              fullWidth
              required
              inputProps={{
                minLength: 3,
                maxLength: 4,
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <CvvIcon width={21} height={15} />
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (
                  !/^[0-9]$/.test(e.key) &&
                  !['Backspace', 'Delete'].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
            />
          </Stack>
        </Stack>
        <TextField
          name='holderName'
          label={t('Name as it appears on the card')}
          placeholder={t('Name as it appears on the card')}
          required
          sx={{ m: 0 }}
          inputProps={{
            minLength: 3,
            maxLength: 140,
            pattern: '*',
          }}
        />
        {/* 
        <TaxIDInputFormik
          name='holderTaxID'
          label={t('CPF or CNPJ')}
          placeholder={t('CPF or CNPJ')}
          sx={{ m: 0 }}
          inputProps={{
            inputMode: 'numeric',
          }}
        /> */}
        <ActionButton
          data-testid='confirm-step'
          disabled={!isValid || isPendingChargeAddPaymentMethod || loading}
          onClick={handleSubmit}
          loading={isPendingChargeAddPaymentMethod || loading}
        >
          {t('Confirm Payment')}
        </ActionButton>
      </Stack>
    </FormikProvider>
  );
};

export default PaymentMethodCreditCardConfirmStep;
